import React, { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment';
import { ReportsContext } from '../../../context/report/ReportContext';
import Paginator from '../../../components/ui/Paginator';
import ReportDateRange from '../../../components/ui/ReportDateRange';
import Message from '../../../components/ui/Message';
import { downloadCsv } from '../../../utils/csv';

const PAGE_SIZE = 25;

const numberFormatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});

const quantityFormatter = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 3
});

const formatAmount = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    return isNaN(num) ? String(value) : numberFormatter.format(num);
};

const formatDateTime = (value) => value ? moment(value).format("DD/MM/YYYY HH:mm") : '';

// Color del estado de Hacienda según su texto
const statusBadge = (status?: string | null) => {
    const value = String(status ?? '').toLowerCase();
    if (/acept/.test(value)) return 'bg-success-subtle text-success-emphasis';
    if (/rechaz|error/.test(value)) return 'bg-danger-subtle text-danger-emphasis';
    if (/pend|proces|envi|recib/.test(value)) return 'bg-warning-subtle text-warning-emphasis';
    return 'bg-secondary-subtle text-secondary-emphasis';
};

const hasMessage = (mensaje) => mensaje && String(mensaje).trim() !== '' && mensaje !== '.';

// Etiquetas de todos los campos del comprobante (orden usado en el CSV y en el detalle)
const headerMap: Record<string, string> = {
    N_FACTURA: "Factura",
    EstadoProceso: "Estado",
    ConsecutivoTipoComprobante: "Tipo",
    NumeroConsecutivoNumeroComprobanteElectronico: "Num CE",
    ClaveFechaFactura: "Fecha Clave",
    Clave: "Clave",
    NumeroConsecutivo: "Consecutivo",
    FechaEmision: "Fecha Emisión",

    ReceptorNombre: "Receptor",
    ReceptorTipo: "Tipo ID",
    ReceptorNumero: "ID",
    ReceptorIdentificacionExtranjero: "ID Extranjero",
    ReceptorCorreoElectronico: "Correo",

    OtrosCargosTipoDocumento: "OC Tipo Doc",
    OtrosCargosNumeroIdentidadTercero: "OC ID Tercero",
    OtrosCargosNombreTercero: "OC Nombre Tercero",
    OtrosCargosDetalle: "OC Detalle",
    OtrosCargosPorcentaje: "OC % Cargo",
    OtrosCargosMontoCargo: "OC Monto",

    ResumenFacturaCodigoTipoMonedaCodigoMoneda: "Moneda",
    ResumenFacturaCodigoTipoMonedaTipoCambio: "Tipo Cambio",

    ResumenFacturaTotalServGravados: "Serv Gravados",
    ResumenFacturaTotalServExentos: "Serv Exentos",
    ResumenFacturaTotalServExonerado: "Serv Exonerados",

    ResumenFacturaTotalMercanciasGravadas: "Merc Gravadas",
    ResumenFacturaTotalMercanciasExentas: "Merc Exentas",
    ResumenFacturaTotalMercExonerada: "Merc Exoneradas",

    ResumenFacturaTotalGravado: "Gravado",
    ResumenFacturaTotalExento: "Exento",
    ResumenFacturaTotalExonerado: "Exonerado",

    ResumenFacturaTotalVenta: "Venta",
    ResumenFacturaTotalDescuentos: "Descuentos",
    ResumenFacturaTotalVentaNeta: "Venta Neta",
    ResumenFacturaTotalImpuesto: "Impuesto",
    ResumenFacturaTotalIVADevuelto: "IVA Devuelto",
    ResumenFacturaTotalOtrosCargos: "Otros Cargos",
    ResumenFacturaTotalComprobante: "Total Comp",

    NormativaNumeroResolucion: "N° Resolución",
    NormativaFechaResolucion: "Fecha Resolución",

    Respuesta: "Respuesta",
    Mensaje: "Mensaje",

    InformacionReferenciaTipoDoc: "Ref Tipo Doc",
    InformacionReferenciaNumero: "Referencia",
    InformacionReferenciaFechaEmision: "Ref Fecha",
    InformacionReferenciaCodigo: "Ref Código",
    InformacionReferenciaRazon: "Razón Ref."
};

// Secciones del modal de detalle
const detailSections: { title: string; icon: string; fields: string[] }[] = [
    {
        title: 'Comprobante', icon: 'bi-receipt',
        fields: ['N_FACTURA', 'ConsecutivoTipoComprobante', 'NumeroConsecutivo', 'NumeroConsecutivoNumeroComprobanteElectronico', 'FechaEmision', 'ClaveFechaFactura', 'Clave', 'EstadoProceso', 'Respuesta'],
    },
    {
        title: 'Receptor', icon: 'bi-person',
        fields: ['ReceptorNombre', 'ReceptorTipo', 'ReceptorNumero', 'ReceptorIdentificacionExtranjero', 'ReceptorCorreoElectronico'],
    },
    {
        title: 'Resumen', icon: 'bi-calculator',
        fields: ['ResumenFacturaCodigoTipoMonedaCodigoMoneda', 'ResumenFacturaCodigoTipoMonedaTipoCambio',
            'ResumenFacturaTotalServGravados', 'ResumenFacturaTotalServExentos', 'ResumenFacturaTotalServExonerado',
            'ResumenFacturaTotalMercanciasGravadas', 'ResumenFacturaTotalMercanciasExentas', 'ResumenFacturaTotalMercExonerada',
            'ResumenFacturaTotalGravado', 'ResumenFacturaTotalExento', 'ResumenFacturaTotalExonerado',
            'ResumenFacturaTotalVenta', 'ResumenFacturaTotalDescuentos', 'ResumenFacturaTotalVentaNeta',
            'ResumenFacturaTotalImpuesto', 'ResumenFacturaTotalIVADevuelto', 'ResumenFacturaTotalOtrosCargos', 'ResumenFacturaTotalComprobante'],
    },
    {
        title: 'Otros cargos', icon: 'bi-plus-slash-minus',
        fields: ['OtrosCargosTipoDocumento', 'OtrosCargosNumeroIdentidadTercero', 'OtrosCargosNombreTercero', 'OtrosCargosDetalle', 'OtrosCargosPorcentaje', 'OtrosCargosMontoCargo'],
    },
    {
        title: 'Referencia', icon: 'bi-link-45deg',
        fields: ['InformacionReferenciaTipoDoc', 'InformacionReferenciaNumero', 'InformacionReferenciaFechaEmision', 'InformacionReferenciaCodigo', 'InformacionReferenciaRazon'],
    },
    {
        title: 'Normativa', icon: 'bi-journal-text',
        fields: ['NormativaNumeroResolucion', 'NormativaFechaResolucion'],
    },
];

const headerDetailMap: Record<string, string> = {
    LineaDetalleNumeroLinea: "Línea",
    LineaDetallePartidaArancelaria: "Partida Arancelaria",
    LineaDetalleCodigo: "Código",
    LineaDetalleCodigoComercialTipo: "Tipo Comercial",
    LineaDetalleCodigoComercialCodigo: "Código Comercial",
    LineaDetalleCantidad: "Cantidad",
    LineaDetalleUnidadMedida: "Unidad",
    LineaDetalleDetalle: "Detalle",
    LineaDetallePrecioUnitario: "Precio Unitario",
    LineaDetalleMontoTotal: "Monto Total",
    LineaDetalleMontoDescuento: "Descuento",
    LineaDetalleNaturalezaDescuento: "Naturaleza Descuento",
    LineaDetalleSubTotal: "SubTotal",
    LineaDetalleBaseImponible: "Base Imponible",
    LineaDetalleImpuestoCodigo: "Código Impuesto",
    LineaDetalleImpuestoCodigoTarifa: "Código Tarifa",
    LineaDetalleImpuestoTarifa: "Tarifa",
    LineaDetalleImpuestoFactor_IVA: "Factor IVA",
    LineaDetalleImpuestoMonto: "Monto Impuesto",
    LineaDetalleImpuestoMontoExportacion: "Monto Exportación",
    LineaDetalleImpuestoExoneracionTipoDocumento: "Tipo Doc Exoneración",
    LineaDetalleImpuestoExoneracionNumeroDocumento: "N° Doc Exoneración",
    LineaDetalleImpuestoExoneracionNombreInstitucion: "Nombre Institución",
    LineaDetalleImpuestoExoneracionFechaEmision: "Fecha Emisión Exoneración",
    LineaDetalleImpuestoExoneracionPorcentajeExoneracion: "% Exoneración",
    LineaDetalleImpuestoExoneracionMontoExoneracion: "Monto Exoneración",
    LineaDetalleImpuestoImpuestoNeto: "Impuesto Neto",
    LineaDetalleMontoTotalLinea: "Total Línea",
    DescripcionBienServicio: "Descripción Bien/Servicio"
};

// Columnas de las líneas que se muestran por defecto en el detalle
const mainDetailColumns = [
    'LineaDetalleNumeroLinea', 'LineaDetalleCodigo', 'LineaDetalleDetalle', 'LineaDetalleCantidad', 'LineaDetalleUnidadMedida',
    'LineaDetallePrecioUnitario', 'LineaDetalleMontoDescuento', 'LineaDetalleSubTotal', 'LineaDetalleImpuestoTarifa',
    'LineaDetalleImpuestoImpuestoNeto', 'LineaDetalleMontoTotalLinea'
];

const camposNumericos = [
    "ResumenFacturaTotalServGravados",
    "ResumenFacturaTotalServExentos",
    "ResumenFacturaTotalServExonerado",
    "ResumenFacturaTotalMercanciasGravadas",
    "ResumenFacturaTotalMercanciasExentas",
    "ResumenFacturaTotalMercExonerada",
    "ResumenFacturaTotalGravado",
    "ResumenFacturaTotalExento",
    "ResumenFacturaTotalExonerado",
    "ResumenFacturaTotalImpuesto",
    "ResumenFacturaTotalComprobante",
    "ResumenFacturaTotalVenta",
    "ResumenFacturaTotalDescuentos",
    "ResumenFacturaTotalVentaNeta",
    "ResumenFacturaTotalOtrosCargos",
    "ResumenFacturaTotalIVADevuelto",
    "OtrosCargosMontoCargo",
    "LineaDetallePrecioUnitario",
    "LineaDetalleMontoTotal",
    "LineaDetalleMontoDescuento",
    "LineaDetalleSubTotal",
    "LineaDetalleBaseImponible",
    "LineaDetalleImpuestoMonto",
    "LineaDetalleImpuestoMontoExportacion",
    "LineaDetalleImpuestoExoneracionMontoExoneracion",
    "LineaDetalleImpuestoImpuestoNeto",
    "LineaDetalleMontoTotalLinea"
];

const formatField = (key: string, value: any) => {
    if (value === null || value === undefined || value === '') return '';
    if (camposNumericos.includes(key)) return formatAmount(value);
    if (key === 'LineaDetalleCantidad') return quantityFormatter.format(Number(value));
    if (key === 'FechaEmision') return formatDateTime(value);
    return String(value);
};

const isNumericColumn = (key: string) => camposNumericos.includes(key) || key === 'LineaDetalleCantidad' || key === 'LineaDetalleImpuestoTarifa';


export const ElectronicInvoiceReport = () => {

    const { invoices, getInvoiceByUserMerchant, getInvoiceDetailById } = useContext(ReportsContext);

    const [buscar, setBuscar] = useState('');
    const [invoice, setInvoice] = useState('');
    const [type, setType] = useState('00');
    const [consecutive, setConsecutive] = useState('');
    const [pageActual, setPageActual] = useState<number>(1);
    const [dateIni, setDateIni] = useState(moment().format("YYYYMMDD"));
    const [dateEnd, setDateEnd] = useState(moment().add(1, 'day').format("YYYYMMDD"));
    const [hasSearched, setHasSearched] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
    const [detalleFactura, setDetalleFactura] = useState<any[]>([]);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [showAllColumns, setShowAllColumns] = useState(false);

    const [errorMessage, setErrorMessage] = useState('');
    const [haciendaMessage, setHaciendaMessage] = useState('');

    useEffect(() => {
        setPageActual(1);
    }, [buscar]);

    const handleFilter = async (e?: React.FormEvent) => {
        e?.preventDefault();
        setIsLoading(true);
        try {
            await getInvoiceByUserMerchant(dateIni, dateEnd, invoice.trim(), type, consecutive.trim());
            setHasSearched(true);
            setPageActual(1);
        } catch (error) {
            setErrorMessage('No se pudo generar el reporte, intente de nuevo.');
        } finally {
            setIsLoading(false);
        }
    }

    const handleCallback = (start, end) => {
        setDateIni(start.format('YYYYMMDD'));
        setDateEnd(moment(end).add(1, 'day').format("YYYYMMDD"))
    }

    const verDetalle = async (inv) => {
        setSelectedInvoice(inv);
        setDetalleFactura([]);
        setShowAllColumns(false);
        setLoadingDetail(true);
        try {
            const data = await getInvoiceDetailById(inv.N_FACTURA, inv.NumeroConsecutivoTipoComprobante);
            setDetalleFactura(data ?? []);
        } catch (error) {
            setSelectedInvoice(null);
            setErrorMessage('No se pudo obtener el detalle de la factura, intente de nuevo.');
        } finally {
            setLoadingDetail(false);
        }
    };

    const closeDetail = () => {
        setSelectedInvoice(null);
        setDetalleFactura([]);
    };

    const filteredData = useMemo(() => {
        const term = buscar.trim().toLowerCase();
        if (!term) return invoices;
        return invoices.filter((inv) =>
            Object.values(inv).some((value) => String(value ?? "").toLowerCase().includes(term))
        );
    }, [invoices, buscar]);

    const totalRecords = filteredData.length;
    const totalPage = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));
    const pageItems = filteredData.slice((pageActual - 1) * PAGE_SIZE, pageActual * PAGE_SIZE);
    const startIndex = totalRecords === 0 ? 0 : (pageActual - 1) * PAGE_SIZE + 1;
    const endIndex = Math.min(pageActual * PAGE_SIZE, totalRecords);

    const handleDownload = () => {
        downloadCsv('ReporteFacturasElectronicas.csv', invoices.map((inv) => {
            const row: Record<string, any> = {};
            Object.keys(headerMap).forEach(key => { row[headerMap[key]] = inv[key]; });
            return row;
        }));
    }

    const detailColumns = detalleFactura.length > 0
        ? (showAllColumns
            ? Object.keys(detalleFactura[0])
            : mainDetailColumns.filter(key => key in detalleFactura[0]))
        : [];

    const renderBody = () => {
        if (isLoading) {
            return (
                <tr><td colSpan={11} className="text-center py-4">
                    <span className="spinner-border spinner-border-sm me-2" /> Generando reporte...
                </td></tr>
            );
        }
        if (!hasSearched) {
            return (
                <tr><td colSpan={11} className="text-center py-4 text-muted">
                    <i className="bi bi-funnel me-2" />Seleccione los filtros y presione "Generar reporte".
                </td></tr>
            );
        }
        if (pageItems.length === 0) {
            return (
                <tr><td colSpan={11} className="text-center py-4 text-muted">
                    {buscar ? 'No se encontraron comprobantes para la búsqueda.' : 'No hay comprobantes para los filtros seleccionados.'}
                </td></tr>
            );
        }
        return pageItems.map((inv, i) => (
            <tr key={`${inv.Clave ?? inv.N_FACTURA}-${i}`}>
                <td className="fw-medium">{inv.N_FACTURA}</td>
                <td style={styles.nowrap}>{inv.ConsecutivoTipoComprobante}</td>
                <td className="text-muted small" style={{ fontFamily: 'monospace' }}>{inv.NumeroConsecutivo}</td>
                <td style={styles.nowrap}>{formatDateTime(inv.FechaEmision)}</td>
                <td>
                    <div style={styles.ellipsis}>{inv.ReceptorNombre || '-'}</div>
                    {inv.ReceptorNumero && <div className="text-muted small">{inv.ReceptorNumero}</div>}
                </td>
                <td>{inv.ResumenFacturaCodigoTipoMonedaCodigoMoneda}</td>
                <td className="text-end" style={styles.nowrap}>{formatAmount(inv.ResumenFacturaTotalVentaNeta)}</td>
                <td className="text-end" style={styles.nowrap}>{formatAmount(inv.ResumenFacturaTotalImpuesto)}</td>
                <td className="text-end fw-semibold" style={styles.nowrap}>{formatAmount(inv.ResumenFacturaTotalComprobante)}</td>
                <td>
                    <span className={`badge rounded-pill ${statusBadge(inv.EstadoProceso)}`}>{inv.EstadoProceso || '-'}</span>
                </td>
                <td style={{ textAlign: 'center', width: 110, whiteSpace: 'nowrap' }}>
                    <button type="button" className="btn btn-sm btn-primary" title="Ver detalle" onClick={() => verDetalle(inv)}>
                        <i className="bi bi-eye" />
                    </button>
                    {hasMessage(inv.Mensaje) && (
                        <button type="button" className="btn btn-sm btn-outline-warning ms-1" title="Ver mensaje de Hacienda" onClick={() => setHaciendaMessage(String(inv.Mensaje))}>
                            <i className="bi bi-chat-left-text" />
                        </button>
                    )}
                </td>
            </tr>
        ));
    };

    return (
        <div className='mt-5 container-fluid px-4' style={styles.container}>

            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                <div>
                    <h3 className="mb-1">Reporte de facturación electrónica</h3>
                    <p className="text-muted mb-0">Comprobantes electrónicos emitidos y su estado ante Hacienda.</p>
                </div>
                <div className="d-flex gap-2 mt-2 mt-md-0">
                    <button type="button" className="btn btn-outline-secondary" onClick={handleDownload} disabled={invoices.length === 0}>
                        <i className="bi bi-download me-1" /> Descargar CSV
                    </button>
                </div>
            </div>

            {/* Filtros */}
            <div className="card border-0 shadow-sm mb-3">
                <div className="card-body">
                    <form className="row g-2 align-items-end" onSubmit={handleFilter}>
                        <div className="col-md-6 col-lg-3">
                            <label className="form-label small text-muted mb-1">Rango de fechas</label>
                            <ReportDateRange onChange={handleCallback} minDate={moment().subtract(1, 'year')} />
                        </div>
                        <div className="col-md-6 col-lg-3">
                            <label className="form-label small text-muted mb-1">Tipo de comprobante</label>
                            <select className="form-select" value={type} onChange={(e) => setType(e.target.value)}>
                                <option value="00">Todos</option>
                                <option value="01">Factura electrónica</option>
                                <option value="04">Tiquete electrónico</option>
                                <option value="03">Nota de crédito</option>
                                <option value="02">Nota de débito</option>
                            </select>
                        </div>
                        <div className="col-6 col-md-4 col-lg-2">
                            <label className="form-label small text-muted mb-1">Factura</label>
                            <input className="form-control" placeholder="N° factura" value={invoice} onChange={(e) => setInvoice(e.target.value)} />
                        </div>
                        <div className="col-6 col-md-4 col-lg-2">
                            <label className="form-label small text-muted mb-1">Consecutivo</label>
                            <input className="form-control" placeholder="Consecutivo" value={consecutive} onChange={(e) => setConsecutive(e.target.value)} />
                        </div>
                        <div className="col-md-4 col-lg-2 d-grid">
                            <button type="submit" className="btn btn-primary" disabled={isLoading}>
                                <i className="bi bi-bar-chart me-1" /> Generar reporte
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Resultados */}
            <div className="card border-0 shadow-sm">
                <div className="card-body">
                    {hasSearched && invoices.length > 0 && (
                        <div className="row g-2 mb-3">
                            <div className="col-md-6">
                                <div className="input-group">
                                    <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                                    <input
                                        className="form-control"
                                        placeholder="Buscar por factura, receptor, clave, estado..."
                                        value={buscar}
                                        onChange={(e) => setBuscar(e.target.value)}
                                    />
                                    {buscar && (
                                        <button className="btn btn-outline-secondary" type="button" title="Limpiar búsqueda" onClick={() => setBuscar('')}>
                                            <i className="bi bi-x-lg" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className='table-responsive'>
                        <table className="table table-hover align-middle mb-0">
                            <thead>
                                <tr className="text-muted small text-uppercase">
                                    <th>Factura</th>
                                    <th>Tipo</th>
                                    <th>Consecutivo</th>
                                    <th style={styles.nowrap}>Fecha emisión</th>
                                    <th>Receptor</th>
                                    <th>Moneda</th>
                                    <th className="text-end" style={styles.nowrap}>Venta neta</th>
                                    <th className="text-end">Impuesto</th>
                                    <th className="text-end">Total</th>
                                    <th>Estado</th>
                                    <th style={{ textAlign: 'center' }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>{renderBody()}</tbody>
                        </table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                        <span className="text-muted small">
                            {!isLoading && totalRecords > 0 ? `Mostrando ${startIndex}-${endIndex} de ${totalRecords} comprobantes` : ''}
                        </span>
                        {totalPage > 1 && !isLoading ? <Paginator totalPage={totalPage} pageActual={pageActual} onChange={(actual) => setPageActual(actual)} /> : <div />}
                    </div>
                </div>
            </div>

            <div style={{ height: '60px' }}></div>

            {/* Modal de detalle */}
            {selectedInvoice && (
                <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }} onClick={closeDetail}>
                    <div className="modal-dialog modal-dialog-scrollable" style={{ maxWidth: "min(1200px, 95vw)" }} onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content border-0">

                            <div className="modal-header">
                                <div>
                                    <h5 className="modal-title mb-1">
                                        {selectedInvoice.ConsecutivoTipoComprobante} #{selectedInvoice.N_FACTURA}
                                    </h5>
                                    <div className="d-flex align-items-center gap-2 small text-muted">
                                        <span className={`badge rounded-pill ${statusBadge(selectedInvoice.EstadoProceso)}`}>{selectedInvoice.EstadoProceso || '-'}</span>
                                        <span>{formatDateTime(selectedInvoice.FechaEmision)}</span>
                                        <span className="fw-semibold text-body">
                                            {selectedInvoice.ResumenFacturaCodigoTipoMonedaCodigoMoneda} {formatAmount(selectedInvoice.ResumenFacturaTotalComprobante)}
                                        </span>
                                    </div>
                                </div>
                                <button type="button" className="btn-close" onClick={closeDetail}></button>
                            </div>

                            <div className="modal-body" style={{ maxHeight: "75vh" }}>

                                {/* Información del comprobante por secciones */}
                                <div className="row g-3 mb-4">
                                    {detailSections.map(section => {
                                        const fields = section.fields.filter(key => formatField(key, selectedInvoice[key]) !== '' && selectedInvoice[key] !== '.');
                                        if (fields.length === 0) return null;
                                        return (
                                            <div className={section.title === 'Resumen' ? 'col-12' : 'col-12 col-lg-6'} key={section.title}>
                                                <div className="border rounded p-3 h-100">
                                                    <h6 className="mb-3"><i className={`bi ${section.icon} me-2 text-primary`} />{section.title}</h6>
                                                    <div className="row g-2">
                                                        {fields.map(key => (
                                                            <div className={section.title === 'Resumen' ? 'col-6 col-md-3' : 'col-12 col-sm-6'} key={key}>
                                                                <div className="text-muted small">{headerMap[key] || key}</div>
                                                                <div className={`small ${key === 'Clave' ? 'text-break' : ''} ${key === 'ResumenFacturaTotalComprobante' ? 'fw-bold' : 'fw-medium'}`}>
                                                                    {formatField(key, selectedInvoice[key])}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Líneas de detalle */}
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="mb-0"><i className="bi bi-list-ul me-2 text-primary" />Líneas de detalle</h6>
                                    {detalleFactura.length > 0 && (
                                        <div className="form-check form-switch mb-0">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="showAllColumns"
                                                checked={showAllColumns}
                                                onChange={(e) => setShowAllColumns(e.target.checked)}
                                            />
                                            <label className="form-check-label small" htmlFor="showAllColumns">Ver todas las columnas</label>
                                        </div>
                                    )}
                                </div>

                                <div className="table-responsive">
                                    <table className="table table-sm table-hover align-middle mb-0">
                                        <thead>
                                            <tr className="text-muted small text-uppercase">
                                                {detailColumns.map((key) => (
                                                    <th
                                                        key={key}
                                                        className={isNumericColumn(key) ? 'text-end' : ''}
                                                        style={{ whiteSpace: 'nowrap', minWidth: key === 'LineaDetalleDetalle' || key === 'DescripcionBienServicio' ? 240 : undefined }}
                                                    >
                                                        {headerDetailMap[key] || key}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loadingDetail ? (
                                                <tr><td colSpan={Math.max(detailColumns.length, 1)} className="text-center py-4">
                                                    <span className="spinner-border spinner-border-sm me-2" /> Cargando detalle...
                                                </td></tr>
                                            ) : detalleFactura.length === 0 ? (
                                                <tr><td colSpan={Math.max(detailColumns.length, 1)} className="text-center py-4 text-muted">
                                                    El comprobante no tiene líneas de detalle.
                                                </td></tr>
                                            ) : (
                                                detalleFactura.map((linea, idx) => (
                                                    <tr key={idx}>
                                                        {detailColumns.map((key) => (
                                                            <td key={key} className={isNumericColumn(key) ? 'text-end' : ''} style={isNumericColumn(key) ? styles.nowrap : undefined}>
                                                                {formatField(key, linea[key])}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="modal-footer">
                                {hasMessage(selectedInvoice.Mensaje) && (
                                    <button className="btn btn-outline-warning me-auto" onClick={() => setHaciendaMessage(String(selectedInvoice.Mensaje))}>
                                        <i className="bi bi-chat-left-text me-1" /> Ver mensaje de Hacienda
                                    </button>
                                )}
                                <button className="btn btn-secondary" onClick={closeDetail}>Cerrar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {haciendaMessage.length > 0 && (
                <Message type='error' title='Mensaje de Hacienda' message={haciendaMessage} closeDialog={() => setHaciendaMessage('')} handleOk={() => setHaciendaMessage('')} backgroundColor='rgba(0,0,0,0.4)' />
            )}

            {errorMessage.length > 0 && (
                <Message type='error' message={errorMessage} closeDialog={() => setErrorMessage('')} handleOk={() => setErrorMessage('')} backgroundColor='rgba(0,0,0,0.4)' />
            )}
        </div>
    );
}


const styles = {
    container: {
        flex: 1,
        marginTop: 10,
    },
    nowrap: {
        whiteSpace: 'nowrap' as const,
    },
    ellipsis: {
        whiteSpace: 'nowrap' as const,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: 220,
    },
};
